import { useEffect, useMemo, useState } from 'react'
import {
  AlertCircle, Loader2, RefreshCw, Shield, ShieldCheck, ShieldX,
  SlidersHorizontal, Search, Sparkles, LockKeyhole, Layers3
} from 'lucide-react'
import { api } from '@/lib/api'
import type { CapabilityView } from '@/lib/api'
import { CapabilityPolicyEditor } from './CapabilityPolicyEditor'
import { cn } from '@/lib/utils'

const reasons: Record<string, string> = {
  principal_restriction: 'Restricted specifically for this person',
  group_restriction: 'Restricted by their role or assigned access profile',
  principal_grant: 'Granted specifically to this person',
  no_authority: 'Not included in their current access',
  super_admin_exemption: 'Super Admin authority — approvals and Worker Lock still apply',
  group_grant: 'Inherited from their role or assigned access profile',
}

const stateLabel: Record<string, string> = {
  default: 'Default',
  custom: 'Custom',
  restricted: 'Restricted',
  unavailable: 'Unavailable',
}

export function CapabilityControl({ principalId }: { principalId: string }) {
  const [result, setResult] = useState<{ principal: string; data?: CapabilityView; error?: string } | null>(null)
  const [revision, setRevision] = useState(0)
  const [query, setQuery] = useState('')
  const [domain, setDomain] = useState('all')
  const [showEditor, setShowEditor] = useState(false)

  useEffect(() => {
    let active = true
    setResult(null)
    api.capabilityView(principalId).then(data => {
      if (data.principal_id !== principalId || !Array.isArray(data.capabilities)) throw new Error('Invalid response')
      if (active) setResult({ principal: principalId, data })
    }).catch(() => {
      if (active) setResult({ principal: principalId, error: 'Access information is unavailable. No permissions have been changed.' })
    })
    return () => { active = false }
  }, [principalId, revision])

  const current = result?.principal === principalId ? result : null
  const data = current?.data

  const domains = useMemo(
    () => data ? [...new Set(data.capabilities.map(c => c.domain))].sort() : [],
    [data]
  )

  const filtered = useMemo(() => {
    if (!data) return []
    const q = query.trim().toLowerCase()
    return data.capabilities.filter(cap =>
      (domain === 'all' || cap.domain === domain) &&
      (!q ||
        cap.display_name.toLowerCase().includes(q) ||
        cap.id.toLowerCase().includes(q) ||
        cap.description.toLowerCase().includes(q))
    )
  }, [data, domain, query])

  const stats = useMemo(() => {
    if (!data) return { allowed: 0, custom: 0, restricted: 0, unavailable: 0 }
    return data.capabilities.reduce((acc, cap) => {
      if (cap.effect === 'allow') acc.allowed++
      if (cap.state === 'custom') acc.custom++
      if (cap.state === 'restricted') acc.restricted++
      if (cap.state === 'unavailable') acc.unavailable++
      return acc
    }, { allowed: 0, custom: 0, restricted: 0, unavailable: 0 })
  }, [data])

  function reasonFor(cap: CapabilityView['capabilities'][number]) {
    if (cap.reason === 'group_grant') {
      const group = cap.evidence.find(e => e.decisive)?.group_name
      return group ? `Inherited from ${group}` : reasons.group_grant
    }
    return reasons[cap.reason] || 'Authority decision recorded by Capability Control'
  }

  return (
    <section className="cap-effective">
      <div className="cap-centre-section-head">
        <div>
          <span className="cap-centre-eyebrow">Effective Access</span>
          <h2>What this person can do</h2>
          <p className="text-xs text-text-muted mt-1">
            This is the current capability result. Viewing it never authorises execution.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.can_administer && !data.super_admin && (
            <button
              className={cn('cap-action-button', showEditor && 'cap-action-button-active')}
              onClick={() => setShowEditor(v => !v)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {showEditor ? 'Close adjustments' : 'Adjust access'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setRevision(v => v + 1)}
            disabled={!current}
            aria-label="Refresh capability information"
            className="cap-icon-button"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!current && (
        <div className="cap-empty-state mt-4" role="status">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading authority…
        </div>
      )}

      {current?.error && (
        <div role="alert" className="cap-centre-alert cap-centre-alert-error mt-4">
          <AlertCircle className="h-4 w-4" /> {current.error}
        </div>
      )}

      {data && (
        <>
          <div className="cap-effective-banner">
            <div className="flex items-center gap-3 min-w-0">
              <span className={cn('cap-mode-orb', data.super_admin && 'cap-mode-orb-super')}>
                {data.super_admin ? <Sparkles className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
              </span>
              <div className="min-w-0">
                <strong>{data.super_admin ? 'Super Admin constitutional authority' : 'Capability policy result'}</strong>
                <span>
                  {data.enforcement_mode === 'enforced'
                    ? 'Enforcement is active for registered operations.'
                    : 'Capability policy is configured, but platform enforcement is not active yet.'}
                </span>
              </div>
            </div>
            <span className={cn('cap-mode-pill', data.enforcement_mode === 'enforced' && 'cap-mode-pill-live')}>
              {data.enforcement_mode === 'enforced' ? 'Enforced' : data.enforcement_mode}
            </span>
          </div>

          <div className="cap-access-stats">
            <div className="cap-access-stat cap-access-stat-allow">
              <ShieldCheck className="h-4 w-4" />
              <strong>{stats.allowed}</strong>
              <span>Allowed</span>
            </div>
            <div className="cap-access-stat cap-access-stat-custom">
              <Layers3 className="h-4 w-4" />
              <strong>{stats.custom}</strong>
              <span>Custom</span>
            </div>
            <div className="cap-access-stat cap-access-stat-restrict">
              <ShieldX className="h-4 w-4" />
              <strong>{stats.restricted}</strong>
              <span>Restricted</span>
            </div>
            <div className="cap-access-stat cap-access-stat-unavailable">
              <LockKeyhole className="h-4 w-4" />
              <strong>{stats.unavailable}</strong>
              <span>Unavailable</span>
            </div>
          </div>

          {showEditor && data.can_administer && !data.super_admin && (
            <div className="cap-editor-drawer">
              <CapabilityPolicyEditor
                key={principalId}
                principalId={principalId}
                onChanged={() => setRevision(v => v + 1)}
              />
            </div>
          )}

          <div className="cap-filter-row">
            <label className="cap-search">
              <Search className="h-4 w-4" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search capabilities..."
                aria-label="Search effective capabilities"
              />
            </label>
            <div className="cap-domain-strip" aria-label="Capability domains">
              <button
                onClick={() => setDomain('all')}
                className={cn('cap-domain-chip', domain === 'all' && 'cap-domain-chip-active')}
              >
                All
              </button>
              {domains.map(item => (
                <button
                  key={item}
                  onClick={() => setDomain(item)}
                  className={cn('cap-domain-chip', domain === item && 'cap-domain-chip-active')}
                >
                  {item.replaceAll('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="cap-centre-placeholder cap-centre-placeholder-compact">
              <Shield className="h-5 w-5" />
              <p>No capabilities match this view.</p>
            </div>
          ) : (
            <div className="cap-capability-grid">
              {filtered.map(cap => {
                const allowed = cap.effect === 'allow'
                return (
                  <article
                    key={cap.id}
                    className={cn(
                      'cap-capability-card',
                      allowed ? 'cap-capability-card-allow' : 'cap-capability-card-deny',
                      cap.state === 'custom' && 'cap-capability-card-custom'
                    )}
                  >
                    <div className="cap-capability-card-top">
                      <span className={cn('cap-capability-icon', allowed ? 'cap-capability-icon-allow' : 'cap-capability-icon-deny')}>
                        {allowed ? <ShieldCheck className="h-4 w-4" /> : <ShieldX className="h-4 w-4" />}
                      </span>
                      <div className="cap-capability-statuses">
                        <span className={cn('cap-effect-pill', allowed ? 'cap-effect-pill-allow' : 'cap-effect-pill-deny')}>
                          {allowed ? 'Allowed' : 'Not allowed'}
                        </span>
                        <span className={cn('cap-state-pill', 'cap-state-' + cap.state)}>
                          {stateLabel[cap.state] || cap.state}
                        </span>
                      </div>
                    </div>

                    <div className="cap-capability-copy">
                      <span className="cap-domain-label">{cap.domain.replaceAll('_', ' ')}</span>
                      <h3>{cap.display_name}</h3>
                      <p>{cap.description}</p>
                    </div>

                    <div className="cap-capability-reason">
                      <span>Why</span>
                      <p>{reasonFor(cap)}</p>
                    </div>

                    <div className="cap-capability-id">{cap.id}</div>
                  </article>
                )
              })}
            </div>
          )}
        </>
      )}
    </section>
  )
}
